import type { PlayfieldRect } from '../circlesPlayfieldGeometry';
import type { CursorTrailSample } from './circlesRenderTypes';
import {
  CURSOR_IDLE_SCALE,
  CURSOR_PRESSED_SCALE,
  CURSOR_SCALE_LERP_RATE,
  CURSOR_TRAIL_MAX,
  TRAIL_SPRING_DAMPING,
  TRAIL_SPRING_STIFFNESS,
} from './circlesRenderConstants';
import type { RenderQuality } from '../../rendering/renderQuality';

interface TrailSpring {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

/**
 * Owns the cursor reticle + trail render state: the spring follower
 * for the trail tail, the per-sample buffer, and the smoothed cursor
 * press-scale. Cursor visuals are purely cosmetic — the engine never
 * sees these, so they live as a dedicated subsystem rather than
 * leaking into shared state.
 */
export class CursorRenderer {
  private cursorTrail: CursorTrailSample[] = [];
  /**
   * Spring follower for the cursor trail head. Lags behind the live
   * cursor via Hooke's law + damping so the trail "trails" with
   * weight rather than copying the cursor's hard-cut motion. `null`
   * until the first cursor sample arrives, and reset to `null`
   * whenever the cursor leaves the canvas so re-entry doesn't
   * snap-spring across the playfield.
   */
  private trailSpring: TrailSpring | null = null;
  /**
   * Smoothly-eased current cursor scale. Updated each frame toward
   * `CURSOR_PRESSED_SCALE` while held / `CURSOR_IDLE_SCALE` while
   * released. Exponential lerp so quick taps still produce a visible
   * pulse but rapid press/release doesn't snap jarringly.
   */
  private cursorScale = CURSOR_IDLE_SCALE;
  private reducedMotion: boolean;
  private quality: RenderQuality;

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.quality = quality;
  }

  /**
   * Append the latest cursor sample to the trail buffer and age the
   * existing samples so older positions fade out.
   *
   * Reduced-motion users get no trail at all (it's a movement-driven
   * animation that conflicts with the system preference). Skipped
   * frames where `cursor === null` (player hasn't moved the mouse
   * onto the canvas yet) clear the buffer so the next move starts
   * fresh rather than smearing back to a stale position.
   */
  updateTrail(cursor: { x: number; y: number } | null, dt: number): void {
    if (this.reducedMotion) {
      this.cursorTrail.length = 0;
      this.trailSpring = null;
      return;
    }
    if (cursor === null) {
      this.cursorTrail.length = 0;
      this.trailSpring = null;
      return;
    }
    if (this.trailSpring === null) {
      // First sample after re-entering the playfield — initialise the
      // follower at the cursor with zero velocity so it doesn't whip
      // across the playfield before settling.
      this.trailSpring = { x: cursor.x, y: cursor.y, vx: 0, vy: 0 };
    } else {
      // Hooke + viscous damping per axis. Slightly under-critical so
      // a hard flick produces one small overshoot before settling
      // — that "weight" is the whole point.
      const ax =
        (cursor.x - this.trailSpring.x) * TRAIL_SPRING_STIFFNESS -
        this.trailSpring.vx * TRAIL_SPRING_DAMPING;
      const ay =
        (cursor.y - this.trailSpring.y) * TRAIL_SPRING_STIFFNESS -
        this.trailSpring.vy * TRAIL_SPRING_DAMPING;
      this.trailSpring.vx += ax * dt;
      this.trailSpring.vy += ay * dt;
      this.trailSpring.x += this.trailSpring.vx * dt;
      this.trailSpring.y += this.trailSpring.vy * dt;
    }
    for (const sample of this.cursorTrail) {
      sample.age += dt;
    }
    // Skip pushing if the spring follower hasn't moved meaningfully
    // since the last sample — a stationary cursor with a settled
    // spring would otherwise stack identical samples on top of each
    // other and over-brighten the dot under the reticle.
    const last = this.cursorTrail[this.cursorTrail.length - 1];
    const sx = this.trailSpring.x;
    const sy = this.trailSpring.y;
    if (!last || (sx - last.x) ** 2 + (sy - last.y) ** 2 > 0.5) {
      this.cursorTrail.push({ x: sx, y: sy, age: 0 });
      if (this.cursorTrail.length > CURSOR_TRAIL_MAX) {
        this.cursorTrail.splice(0, this.cursorTrail.length - CURSOR_TRAIL_MAX);
      }
    }
    // Drop samples older than the trail's max-age so a slow-moving
    // cursor doesn't leave an unbounded comet of stale points.
    while (this.cursorTrail.length > 0 && this.cursorTrail[0].age > 0.4) {
      this.cursorTrail.shift();
    }
  }

  drawTrail(ctx: CanvasRenderingContext2D, field: PlayfieldRect): void {
    if (this.cursorTrail.length < 2) {
      return;
    }
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Render as a series of short segments with declining alpha. A
    // single stroked path with `globalAlpha` would be cheaper but
    // wouldn't fade smoothly along the polyline.
    for (let i = 1; i < this.cursorTrail.length; i++) {
      const a = this.cursorTrail[i - 1];
      const b = this.cursorTrail[i];
      const t = i / this.cursorTrail.length;
      const alpha = Math.max(0, t * (1 - b.age / 0.4));
      if (alpha <= 0.02) {
        continue;
      }
      ctx.strokeStyle = `rgba(22, 224, 255, ${alpha * 0.55})`;
      ctx.lineWidth = 2 + 4 * t;
      ctx.beginPath();
      ctx.moveTo(field.x + a.x * field.scale, field.y + a.y * field.scale);
      ctx.lineTo(field.x + b.x * field.scale, field.y + b.y * field.scale);
      ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * Ease the cursor scale toward its target each frame. Held → grows
   * to `CURSOR_PRESSED_SCALE`, released → shrinks back to
   * `CURSOR_IDLE_SCALE`. Reduced-motion users get no animation at
   * all (the cursor stays at idle size) to respect the system pref.
   *
   * Closed-form exponential lerp (`1 - exp(-rate * dt)`) keeps the
   * easing frame-rate independent — same feel at 30fps and 144fps.
   */
  updateScale(pressed: boolean, dt: number): void {
    if (this.reducedMotion) {
      this.cursorScale = CURSOR_IDLE_SCALE;
      return;
    }
    const target = pressed ? CURSOR_PRESSED_SCALE : CURSOR_IDLE_SCALE;
    const k = 1 - Math.exp(-CURSOR_SCALE_LERP_RATE * dt);
    this.cursorScale += (target - this.cursorScale) * k;
  }

  /**
   * Cursor reticle at the player's most recent pointer position. Drawn
   * with a small glowing ring + crosshair so the player can find it
   * instantly even on a busy frame.
   *
   * Reticle scales with the smoothed press-scale so a held tap surface
   * (mouse / Z / X / Space) visibly enlarges the cursor and releasing
   * eases it back. Glow blur scales too so the cursor's overall
   * "weight" matches the size pulse.
   */
  draw(
    ctx: CanvasRenderingContext2D,
    cursor: { x: number; y: number } | null,
    field: PlayfieldRect,
  ): void {
    if (!cursor) {
      return;
    }
    const cx = field.x + cursor.x * field.scale;
    const cy = field.y + cursor.y * field.scale;
    const scale = this.cursorScale;
    const r = 10 * scale;
    // Press intensity 0→1 maps idle→fully pressed. Used to brighten
    // the glow + outer crosshair at peak press so the press cue is
    // both *bigger* and *brighter*.
    const pressIntensity = Math.max(
      0,
      Math.min(
        1,
        (scale - CURSOR_IDLE_SCALE) / (CURSOR_PRESSED_SCALE - CURSOR_IDLE_SCALE),
      ),
    );
    ctx.save();
    ctx.strokeStyle = '#ffffff';
    if (this.quality.chromeShadows) {
      ctx.shadowColor = '#16e0ff';
      ctx.shadowBlur = 12 + 14 * pressIntensity;
    }
    ctx.lineWidth = 2 + 0.8 * pressIntensity;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = `rgba(255, 255, 255, ${0.75 + 0.25 * pressIntensity})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx - r * 1.6, cy);
    ctx.lineTo(cx - r * 0.5, cy);
    ctx.moveTo(cx + r * 0.5, cy);
    ctx.lineTo(cx + r * 1.6, cy);
    ctx.moveTo(cx, cy - r * 1.6);
    ctx.lineTo(cx, cy - r * 0.5);
    ctx.moveTo(cx, cy + r * 0.5);
    ctx.lineTo(cx, cy + r * 1.6);
    ctx.stroke();
    ctx.restore();
  }
}
